import { createPollInterpreter, type IMediaAnalytics, type ITracking, type PollInterpreter } from 'components-machine';
import { useContext, useEffect, useMemo, useState } from 'react';
import type { Poll } from 'schema/poll';
import { ICState } from 'schema/state';
import { getLoginProvider } from '../../auth';
import { useErrorTrackingContext } from '../../contexts/errorTrackingContext';
import { RootContext } from '../../contexts/rootContext';
import { useVariableContext } from '../../contexts/variableContext';
import { isDebugModeEnabled } from '../../debug';
import { useFeatureFlagContext } from '../../featureFlag';
import { hasPendingEvents, resolveAllPendingEvents } from '../../hooks/useMediaAnalytics';
import intl from '../../locale';
import { Tracking } from '../../pixel';
import { sendInitPoll } from '../../reducer/sendInitPoll';
import { sendNextStep } from '../../reducer/sendNextStep';
import { PollStoragePool } from '../../store/pollStorage';
import { invokeActionHandler } from './handlers';

export function usePollInterpreter({ poll }: { poll: Poll }): {
  interpreter: PollInterpreter;
  tracking: ITracking;
  state: ICState;
  poll: Poll;
} {
  const { trackingEngine, schemaEngine } = useContext(RootContext);
  const { variables } = useVariableContext();
  const { machineInspector } = useFeatureFlagContext();
  const { sendError } = useErrorTrackingContext();

  const { interpreter, tracking }: { interpreter: PollInterpreter; tracking: ITracking } = useMemo(
    () =>
      createPollInterpreter({
        poll,
        variables,
        trackingEngine,
        schemaEngine,
        devTools: machineInspector,
        Tracking,
        env: {
          APP_EXTERNAL_LOGIC_RULES_API_URL: window.APP_EXTERNAL_LOGIC_RULES_API_URL,
          APP_IC_CONTEXT_URL: window.APP_IC_CONTEXT_URL,
          POLL_STORAGE_UPDATE_TIMEOUT: Number(window.POLL_STORAGE_UPDATE_TIMEOUT),
        },
        sendError,
        sendNextStep,
        onEvent: e => {
          if (machineInspector) {
            // eslint-disable-next-line no-console
            console.log(e, Date.now());
          }
        },
        sendInitPoll,
        isDebugMode: isDebugModeEnabled(),
        PollStoragePool,
        OAuth: getLoginProvider(),
        mediaAnalitics: { hasPendingEvents, resolveAllPendingEvents } satisfies IMediaAnalytics,
        intl: {
          TextFieldContentHiddenIntl: intl.formatMessage({ id: 'System.TextFieldContentHidden' }),
          RequiredAnswersNotGiven: intl.formatMessage({ id: 'System.RequiredAnswersNotGiven' }),
          PleaseSelectAtLeastAnswers: args => intl.formatMessage({ id: 'System.PleaseSelectAtLeastAnswers' }, args),
          PollRankAddOther: intl.formatMessage({ id: 'PollRank.AddOther' }),
          Other: intl.formatMessage({ id: 'System.Other' }),
          FillingFieldMandatory: args => intl.formatMessage({ id: 'System.FillingFieldMandatory' }, args),
          MaxAnswersCountReached: intl.formatMessage({ id: 'System.MaxAnswersCountReached' }),
          VideoPercentToUnlockError: 'VIDEO_LOCKED_ERROR',
          AudioPercentToUnlockError: 'AUDIO_LOCKED_ERROR',
          TextLimitError: intl.formatMessage({ id: 'System.TextLimitError' }),
          CardSortingRequiredError: intl.formatMessage({ id: 'CardSortingCategory.RequiredError' }),
          CardSortingAllowUncompleteError: intl.formatMessage({ id: 'CardSortingCategory.AllowUncompleteError' }),
        },
        invokeAction: invokeActionHandler,
        externalLogicRules: getInjectedExternalLogicRules(),
        preinitPollsIc: getPreinitPollsIc(),
      }),
    [],
  );

  const [transformedPoll, setTransformedPoll] = useState<Poll>(interpreter.getSnapshot().context.poll);
  const [state, setState] = useState<ICState>(interpreter.getSnapshot().context.state);

  useEffect(() => {
    interpreter.start();
    interpreter.subscribe(({ context }) => {
      setTransformedPoll(context.poll);
      setState(context.state);
    });
    return () => {
      interpreter.stop();
    };
  }, [interpreter]);

  return {
    poll: transformedPoll,
    interpreter,
    tracking,
    state,
  };
}

function getInjectedExternalLogicRules() {
  try {
    return window.__pollComputedLogicRules;
  } catch {
    return undefined;
  }
}

function getPreinitPollsIc() {
  const url = new URL(location.href);
  return url.searchParams.get('preinit-polls-ic');
}
